import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

type Step = {
  num: string;
  title: string;
  tags: string[];
  details: string[];
};

const steps: Step[] = [
  {
    num: "01",
    title: "Attention",
    tags: ["Contenu", "Vidéo", "Branding"],
    details: ["Concepts et tournages", "Formats courts et longs", "Identité et message"],
  },
  {
    num: "02",
    title: "Acquisition",
    tags: ["Publicité", "Lead Gen"],
    details: ["Campagnes Meta et Google", "Créatifs testés en continu", "Coût par lead suivi"],
  },
  {
    num: "03",
    title: "Conversion",
    tags: ["Landing", "Funnel"],
    details: ["Pages dédiées à l'offre", "Parcours simplifié", "Formulaires qualifiants"],
  },
  {
    num: "04",
    title: "CRM",
    tags: ["Pipeline", "Données"],
    details: ["Chaque lead centralisé", "Étapes de vente claires", "Aucune opportunité perdue"],
  },
  {
    num: "05",
    title: "Suivi",
    tags: ["Email", "SMS", "Automatisation"],
    details: ["Relances automatisées", "Rappels de rendez-vous", "Réactivation des anciens leads"],
  },
  {
    num: "06",
    title: "Croissance",
    tags: ["Analyse", "Optimisation", "Scale"],
    details: ["Lecture mensuelle des chiffres", "Budget réalloué vers ce qui performe", "Scale des campagnes gagnantes"],
  },
];

const EcosystemV2 = () => {
  const [active, setActive] = useState<string | null>(null);

  return (
    <section
      id="approche"
      aria-labelledby="ecosysteme-titre"
      className="relative scroll-mt-24 overflow-hidden bg-secondary/20 py-14 md:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_45%_55%_at_85%_10%,hsl(var(--primary)/0.07)_0%,transparent_70%)]"
      />

      <div className="relative mx-auto max-w-[1280px] px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-primary">L'écosystème 80/20</p>
          <h2
            id="ecosysteme-titre"
            className="mt-4 text-[1.7rem] font-semibold leading-[1.12] tracking-tight text-foreground md:mt-5 md:text-5xl"
          >
            De l'attention jusqu'à la vente.
          </h2>
          <p className="mt-4 text-[0.95rem] leading-[1.55] text-muted-foreground md:mt-5 md:text-lg">
            Une seule équipe connecte chaque étape.
          </p>
        </motion.div>
      </div>

      {/* Un même parcours, adapté à la largeur disponible */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className="relative mx-auto mt-8 max-w-[1280px] px-5 md:mt-16 md:px-8"
      >
        {/* Ligne fine traversant tout le parcours */}
        <div aria-hidden="true" className="absolute inset-x-5 top-0 h-px bg-primary/30 md:inset-x-8" />

        <ol className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 lg:grid-cols-6">
          {steps.map((step) => {
            const isActive = active === step.num;
            const dimmed = active !== null && !isActive;
            return (
              <li
                key={step.num}
                className={`group relative min-w-0 border-l border-border/50 px-4 pt-6 pb-2 first:border-l-0 transition-all duration-500 ${
                  dimmed ? "opacity-35" : "opacity-100"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`absolute -top-1 left-4 h-2 w-2 rounded-full transition-all duration-500 ${
                    isActive ? "scale-125 bg-primary" : "bg-primary/50"
                  }`}
                />
                <span className="block font-mono text-[11px] tracking-[0.2em] text-primary">{step.num}</span>
                <h3 className="mt-3 text-xl font-semibold text-foreground">
                  <Button
                    variant="ghost"
                    onClick={() => setActive(isActive ? null : step.num)}
                    aria-expanded={isActive}
                    aria-controls={`ecosystem-details-${step.num}`}
                    className="h-auto justify-start whitespace-normal p-0 text-left text-xl font-semibold hover:bg-transparent hover:text-primary"
                  >
                    {step.title}
                  </Button>
                </h3>
                <p className="mt-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70">
                  {step.tags.join(" · ")}
                </p>

                <div
                  id={`ecosystem-details-${step.num}`}
                  className={`overflow-hidden transition-all duration-500 ${
                    isActive ? "mt-4 max-h-40 opacity-100" : "mt-0 max-h-0 opacity-0"
                  }`}
                >
                  <ul className="space-y-1.5">
                    {step.details.map((detail) => (
                      <li key={detail} className="text-sm leading-snug text-muted-foreground">
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ol>
      </motion.div>

      <div className="relative mx-auto max-w-[1280px] px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mt-12 max-w-3xl md:mt-24"
        >
          <p className="text-xl font-semibold leading-snug tracking-tight text-foreground md:text-3xl">
            Notre rôle est de connecter les étapes, pas simplement d'exécuter un morceau.
          </p>
          <p className="mt-4 text-[0.95rem] leading-[1.55] text-muted-foreground md:mt-5 md:text-base">
            On mesure notre travail à l'objectif atteint, pas seulement aux livrables produits.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemV2;
